/**
 * Tarife.
 *
 * Eine Stelle, an der Preis und Grenzen zusammen stehen. Vorher lagen die
 * Grenzen als Vorgabewerte in der Datenbank (`scan_page_limit` auf 10 fuer
 * alle) und der Preis nur im Text der Startseite. Beides auseinanderzuhalten
 * ist eine Fehlerquelle, sobald der erste Kunde zahlt.
 *
 * Herleitung der Zahlen steht in `docs/preismodell.md`.
 */

export type PlanKey = 'free' | 'small' | 'medium' | 'large' | 'xlarge' | 'max';

export interface PlanLimits {
  key: PlanKey;
  /** Anzeigename im Dashboard und auf der Rechnung. */
  label: string;
  /** Preis je Domain und Monat in Cent. Kleinunternehmer: keine Umsatzsteuer. */
  priceCents: number;
  /** Obergrenze der Unterseiten je Domain. */
  subpageLimit: number;
  /** Seiten, die ein einzelner Scanlauf hoechstens besucht. */
  scanPageLimit: number;
  /** Anzahl Domains, die in diesem Tarif angelegt werden duerfen. */
  siteLimit: number;
  /** Inbegriffene Auslieferungen je Monat ueber alle Domains. */
  deliveryAllowance: number;
  /** Scanturnus in Tagen. 0 bedeutet nur von Hand. */
  scanIntervalDays: number;
  /** false blendet die Herkunftsangabe im Banner aus. */
  brandingRequired: boolean;
}

/**
 * Aufschlag je angefangener weiterer Million Auslieferungen, in Cent.
 *
 * Der Einkauf liegt bei rund 30 Cent je Million. Der Aufschlag deckt das mit
 * dreifacher Reserve und bleibt trotzdem der kleinstmoegliche Schritt, den man
 * einem Kunden zumuten kann.
 */
export const DELIVERY_OVERAGE_CENTS_PER_MILLION = 100;

export const PLANS: Record<PlanKey, PlanLimits> = {
  free: {
    key: 'free',
    label: 'Frei',
    priceCents: 0,
    subpageLimit: 50,
    scanPageLimit: 25,
    siteLimit: 1,
    deliveryAllowance: 100_000,
    scanIntervalDays: 30,
    brandingRequired: true,
  },
  small: {
    key: 'small',
    label: 'Klein',
    // 7 statt 5 Euro: gleiche Hoehe wie Cookiebot Premium Lite, dafuer mit
    // doppeltem Auslieferungskontingent und ohne Aufschlag fuer Subdomains.
    priceCents: 700,
    subpageLimit: 50,
    scanPageLimit: 25,
    siteLimit: 1,
    deliveryAllowance: 250_000,
    scanIntervalDays: 30,
    brandingRequired: false,
  },
  medium: {
    key: 'medium',
    label: 'Mittel',
    priceCents: 1200,
    subpageLimit: 350,
    scanPageLimit: 60,
    siteLimit: 3,
    deliveryAllowance: 1_000_000,
    scanIntervalDays: 30,
    brandingRequired: false,
  },
  large: {
    key: 'large',
    label: 'Groß',
    priceCents: 2400,
    subpageLimit: 3_500,
    scanPageLimit: 120,
    siteLimit: 10,
    deliveryAllowance: 3_000_000,
    scanIntervalDays: 7,
    brandingRequired: false,
  },
  xlarge: {
    key: 'xlarge',
    label: 'Sehr groß',
    priceCents: 3900,
    subpageLimit: 7_000,
    scanPageLimit: 200,
    siteLimit: 25,
    deliveryAllowance: 8_000_000,
    scanIntervalDays: 7,
    brandingRequired: false,
  },
  max: {
    key: 'max',
    label: 'Maximal',
    priceCents: 6900,
    // Kein hartes Ende nach oben, aber ein Wert, gegen den sich rechnen laesst.
    subpageLimit: 1_000_000,
    scanPageLimit: 300,
    siteLimit: 100,
    deliveryAllowance: 20_000_000,
    scanIntervalDays: 7,
    brandingRequired: false,
  },
};

export const PLAN_ORDER: PlanKey[] = ['free', 'small', 'medium', 'large', 'xlarge', 'max'];

export function istPlanKey(wert: string): wert is PlanKey {
  return Object.prototype.hasOwnProperty.call(PLANS, wert);
}

/**
 * Tarif zu einem gespeicherten Wert.
 *
 * Faellt auf "free" zurueck, weil ein unbekannter Tarif nie zu mehr
 * Berechtigung fuehren darf als der kleinste.
 */
export function planFuer(wert: string | null | undefined): PlanLimits {
  return wert && istPlanKey(wert) ? PLANS[wert] : PLANS.free;
}

/**
 * Kleinster Tarif, der die gemessene Seitenzahl noch traegt.
 *
 * Wird nur zum Vorschlagen benutzt, nie zum automatischen Hochstufen. Die
 * Zusage auf der Preisseite lautet ausdruecklich, dass die Rechnung erst nach
 * Zustimmung steigt.
 */
export function empfohlenerPlan(unterseiten: number): PlanLimits {
  for (const key of PLAN_ORDER) {
    if (key === 'free') continue;
    if (unterseiten <= PLANS[key].subpageLimit) return PLANS[key];
  }
  return PLANS.max;
}

/** Aufschlag in Cent fuer Auslieferungen ueber dem Kontingent. */
export function mehrkostenAuslieferungen(auslieferungen: number, kontingent: number): number {
  const ueber = auslieferungen - kontingent;
  if (ueber <= 0) return 0;
  return Math.ceil(ueber / 1_000_000) * DELIVERY_OVERAGE_CENTS_PER_MILLION;
}
